import { Button, Input } from "@/js/glidespec";
import useBreakpoint from "@/js/Hooks/BreakpointHook";
import { useToaster } from "@/js/Providers/ToasterProvider";
import { useCampaigns } from "@/js/resources";
import TrashIcon from "@/svg/trash-regular.svg?react";
import { Form, setFormValues, SubmitHandler } from "@enymo/react-form-component";
import { Column, Row } from "@enymo/react-layout";
import axios, { AxiosError } from "axios";
import React, { useEffect } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import route from "ziggy-js";

interface Submit {
    webhook_targets: {
        id?: number,
        endpoint: string
    }[]
}

export default function Webhooks() {
    const {t} = useTranslation();
    const mobile = useBreakpoint(768);
    const form = useForm<Submit>({
        defaultValues: {
            webhook_targets: [{
                endpoint: ""
            }]
        }
    });
    const {fields, append, remove} = useFieldArray({control: form.control, name: "webhook_targets"});
    const {campaignId} = useParams();
    const [campaign, {update}] = useCampaigns({id: Number(campaignId)});
    const toast = useToaster();

    useEffect(() => {
        setFormValues(form, {
            webhook_targets: (campaign?.webhook_targets.length ?? 0) > 0 ? campaign?.webhook_targets : undefined
        })
    }, [campaign, form]);

    const handleSubmit: SubmitHandler<Submit> = async ({webhook_targets}) => {
        await update({webhook_targets});
        toast({
            type: "success",
            title: t("campaigns.settings.webhooks.saved.title"),
            text: t("campaigns.settings.webhooks.saved.text")
        });
    }

    const handleTest = async () => {
        try {
            await axios.post(route("campaigns.test-webhook", {campaign: campaignId}));
            toast({
                type: "success",
                title: t("campaigns.settings.webhooks.test.success.title"),
                text: t("campaigns.settings.webhooks.test.success.text")
            });
        }
        catch (e) {
            if (e instanceof AxiosError && e.response?.status === 400) {
                toast({
                    type: "error",
                    title: t("campaings.settings.webhooks.test.fail.title"),
                    text: t("campaigns.settings.webhooks.test.fail.text")
                });
            }
        }
    }

    return (
        <Form form={form} onSubmit={handleSubmit}>
            <Column maxWidth="550px" gap="58px" padding={mobile ? "35px 16px" : "35px 50px"}>
                <Column gap="16px">
                    <Column gap="10px">
                        <label className="input-label">{t("campaigns.settings.webhooks.label")}</label>
                        <Column gap="16px">
                            {fields.map(({id}, index) => (
                                <Row key={id} vAlign="center" gap="12px">
                                    <Input flex={1} name={`webhook_targets.${index}.endpoint`} options={{
                                        required: t("campaigns.settings.webhooks.endpoint.required")
                                    }} />
                                    {fields.length > 1 && (
                                        <button type="button" onClick={() => remove(index)}>
                                            <TrashIcon width="12px" className="error" />
                                        </button>
                                    )}
                                </Row>
                            ))}
                        </Column>
                    </Column>
                    {fields.length < 3 && (
                        <Row>
                            <Button variant="link" onClick={() => append({
                                endpoint: ""
                            })}>{t("campaigns.settings.webhooks.add")}</Button>
                        </Row>
                    )}
                </Column>
                <Row align="end" vAlign="center" gap="16px">
                    <Button variant="secondary" onClick={handleTest}>{t("campaigns.settings.webhooks.test")}</Button>
                    <Button variant="primary" submit>{t("save")}</Button>
                </Row>
            </Column>
        </Form>
    )
}